import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { clearIframeUrl } from "slices/iframeSlice";
import { getEcho } from "services/echo";
import { AlertTriangle, ArrowLeft } from "lucide-react";
import { SubmitButton } from "components/common/buttons/SubmitButton";

// Returns the base iframe URL with its `name` query param overridden by the
// user-chosen display name. 100ms locks the name field when it is passed in the
// URL, so we collect/confirm the name on our own screen first (see withName usage).
const withName = (url, name) => {
  try {
    const u = new URL(url);
    u.searchParams.set("name", name.trim());
    return u.toString();
  } catch {
    return url;
  }
};

const readNameFromUrl = (url) => {
  try {
    return new URL(url).searchParams.get("name") || "";
  } catch {
    return "";
  }
};

const ClientJoinRoomCall = () => {
  const { id } = useParams();
  const iframeUrl = useSelector((state) => state.iframe.iframeUrl);
  const dispatch = useDispatch();
  const [sessionEnded, setSessionEnded] = useState(false);
  const [interpreterDropped, setInterpreterDropped] = useState(false);
  const [joined, setJoined] = useState(false);
  const [displayName, setDisplayName] = useState("");

  // Prefill the editable name field from the account name the backend baked
  // into the iframe URL, once the (persisted) URL is available.
  useEffect(() => {
    if (iframeUrl) setDisplayName(readNameFromUrl(iframeUrl));
  }, [iframeUrl]);

  useEffect(() => {
    if (!id) return;
    const echo = getEcho();
    const channelName = `appointment.${id}`;
    const channel = echo.channel(channelName);
    channel
      .listen(".session-ended", () => {
        dispatch(clearIframeUrl());
        setSessionEnded(true);
        setInterpreterDropped(false);
      })
      .listen(".interpreter-dropped", () => {
        setInterpreterDropped(true);
      })
      .listen(".interpreter-rejoined", () => {
        setInterpreterDropped(false);
      });
    return () => {
      channel.stopListening(".session-ended");
      channel.stopListening(".interpreter-dropped");
      channel.stopListening(".interpreter-rejoined");
      echo.leave(channelName);
    };
  }, [id, dispatch]);

  return (
    <div className="content" style={{ paddingLeft: 0, paddingRight: 0, paddingBottom: 0, display: "flex", flexDirection: "column", height: "calc(100vh - 100px)" }}>
      {interpreterDropped && (
        <div style={{
          background: "#fff3cd",
          borderBottom: "1px solid #ffc107",
          padding: "12px 20px",
          display: "flex",
          alignItems: "flex-start",
          gap: 12,
          flexShrink: 0,
        }}>
          <AlertTriangle size={20} color="#856404" style={{ flexShrink: 0, marginTop: 2 }} />
          <div style={{ flex: 1 }}>
            <strong style={{ display: "block", color: "#856404", marginBottom: 2, fontSize: 14 }}>
              Interpreter temporarily disconnected
            </strong>
            <span style={{ fontSize: 13, color: "#533f03", lineHeight: 1.5 }}>
              The interpreter has been disconnected from the call. Please hold, another interpreter will join shortly
            </span>
          </div>
        </div>
      )}
      <div
        className="container-fluid d-flex flex-column align-items-center justify-content-center bg-light p-0"
        style={{ flex: 1, width: "100%", position: "relative" }}
      >
        {iframeUrl && !sessionEnded ? (
          joined ? (
            <>
              <button
                type="button"
                onClick={() => setJoined(false)}
                style={{
                  position: "absolute",
                  top: 12,
                  left: 12,
                  zIndex: 2,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  background: "rgba(17, 24, 39, 0.72)",
                  color: "#fff",
                  border: "none",
                  borderRadius: 20,
                  padding: "6px 12px",
                  fontSize: 13,
                  fontWeight: 500,
                  cursor: "pointer",
                }}
              >
                <ArrowLeft size={15} /> Edit name
              </button>
              <iframe
                title="100ms-app"
                allow="camera *;microphone *;display-capture *"
                src={withName(iframeUrl, displayName)}
                className="w-100 h-100 border-0"
              ></iframe>
            </>
          ) : (
            <div
              style={{
                background: "#fff",
                borderRadius: 12,
                boxShadow: "0 2px 16px rgba(0,0,0,0.08)",
                padding: "32px 28px",
                width: "100%",
                maxWidth: 420,
                display: "flex",
                flexDirection: "column",
                gap: 16,
              }}
            >
              <div style={{ textAlign: "center" }}>
                <h5 style={{ margin: 0, fontWeight: 600 }}>Join the video call</h5>
                <p style={{ margin: "6px 0 0", fontSize: 14, color: "#6b7280" }}>
                  Confirm the name others will see in the call.
                </p>
              </div>
              <div>
                <label
                  htmlFor="join-display-name"
                  style={{ display: "block", fontSize: 14, fontWeight: 500, marginBottom: 6 }}
                >
                  Your display name
                </label>
                <input
                  id="join-display-name"
                  type="text"
                  className="form-control"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && displayName.trim()) setJoined(true);
                  }}
                  placeholder="Enter your name"
                  autoFocus
                />
              </div>
              <SubmitButton
                buttonType="button"
                btnText="Join Video Call"
                disabled={!displayName.trim()}
                onClick={() => setJoined(true)}
                className="w-100 justify-content-center"
              />
            </div>
          )
        ) : (
          <p>{sessionEnded ? "The session has been ended by the host." : "No video call in progress."}</p>
        )}
      </div>
    </div>
  );
};

export default ClientJoinRoomCall;
